import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Dispatch } from 'redux'

import type {
  PickerSource,
  SelectionType,
} from 'slices/aiConversations/pickerTypes'

import toBackendOperator from 'slices/aiSegmentBuilder/picker/toBackendOperator'

import {
  DEFAULT_GROUP_ID,
  EndpointConfig,
  initializeGroup,
  initializePicker,
  loadBottomData,
  loadTopData,
  OperatorOption,
  removePicker,
  selectBottomOperator,
  selectBottomSelectedItems,
  selectBottomSelectedItemsData,
  selectGroupDisabled,
  selectGroupLevel1Id,
  selectGroupLevel2Id,
  selectGroupLevel2OperatorOptions,
  selectGroupPickerId,
  selectGroupTopOperator,
  selectNextAvailablePickerId,
  selectPickerIdsForGroup,
  selectTopSelectedItem,
  selectTopSelectedItemData,
  setBottomDisabled,
  setBottomOperator,
  setBottomSelectedItems,
  setGroupLevel1Config,
  setGroupLevel2Config,
  setGroupPickerId,
  setGroupSubtitle,
  setGroupTitle,
  setGroupTopOperator,
  setTopSelectedItem,
} from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Server Response Types
// ═══════════════════════════════════════════════════════════════════════════

export interface ServerOperatorOption {
  id: string
  text: string
  requiresValues?: boolean
}

export interface ServerLevelConfig {
  id: string
  label: string
  selectionType: SelectionType | null
  operatorOptions: ServerOperatorOption[]
  endpoint: string | null
  params: Record<string, unknown>
  /** Field name to use as the unique key/id from API response */
  keyField: string
  /** Field name to use as the display value from API response */
  valueField: string
  operator?: string
  subtitle?: string
  sources?: PickerSource[]
  defaultSource?: string
}

export interface ServerSelection {
  level_1: string
  level_1_label?: string
  operator: string
  level_2: string[]
}

export interface ServerPickerResponse {
  picker_id: string
  type: string
  title: string
  subtitle: string
  level_1: ServerLevelConfig
  level_2?: ServerLevelConfig
  selections: ServerSelection[]
}

// ═══════════════════════════════════════════════════════════════════════════
// Extracted Data Types (for internal use)
// ═══════════════════════════════════════════════════════════════════════════

export interface ExtractedPickerData {
  pickerId: number
  level1Key: string | null
  level1Name: string | null
  operator: string
  level2Keys: string[]
  level2Names: string[]
}

export interface ExtractedGroupData {
  groupId: string
  topOperator: string
  disabled: boolean
  pickers: ExtractedPickerData[]
}

// ═══════════════════════════════════════════════════════════════════════════
// Submission Payload Types (matches AIConversation format)
// ═══════════════════════════════════════════════════════════════════════════

export interface ParentSelection {
  parent_id: string
  operator?: string
  child_ids?: string[]
}

export interface PickerLevelSubmission {
  id: string
  operator: string
  selection_ids: string[]
  parent_selections?: ParentSelection[]
}

export interface PickerSubmissionPayload {
  picker_id: string
  levels: PickerLevelSubmission[]
}

// ═══════════════════════════════════════════════════════════════════════════
// Operator Conversion Functions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convert backend operator to UI operator on server load.
 * Reserved for future use - currently returns operator unchanged.
 *
 * Potential future conversions:
 * - 'skip' (text: "any") → 'any' (text: "any of these") - shows bottom selector
 * - 'empty' (text: "no") → 'none' (text: "none of these") - shows bottom selector
 */
export const fromBackendOperator = (operator: string): string => {
  // No conversions for now - return as-is
  return operator
}

// ═══════════════════════════════════════════════════════════════════════════
// Loading Functions (pure functions that dispatch actions)
// ═══════════════════════════════════════════════════════════════════════════

/** A level that reads from its sources has no endpoint of its own */
export const endpointConfigOf = (
  level: ServerLevelConfig
): EndpointConfig | null =>
  level.endpoint === null
    ? null
    : { endpoint: level.endpoint, params: level.params }

/**
 * Load group configuration for both levels.
 * This sets up group-specific config like ids, labels, operator options, and endpoint configs.
 */
export const loadGroupConfig = (
  dispatch: Dispatch,
  groupId: string,
  level1Config: ServerLevelConfig,
  level2Config: ServerLevelConfig
): void => {
  // Set level 1 (top selector) config for this group
  dispatch(
    setGroupLevel1Config({
      groupId,
      id: level1Config.id,
      label: level1Config.label,
      operatorOptions: level1Config.operatorOptions,
      endpointConfig: endpointConfigOf(level1Config),
      keyField: level1Config.keyField,
      valueField: level1Config.valueField,
      sources: level1Config.sources,
      defaultSource: level1Config.defaultSource,
    })
  )

  // Set level 2 (bottom selector) config for this group
  dispatch(
    setGroupLevel2Config({
      groupId,
      id: level2Config.id,
      label: level2Config.label,
      operatorOptions: level2Config.operatorOptions,
      endpointConfig: endpointConfigOf(level2Config),
      subtitle: level2Config.subtitle ?? null,
      keyField: level2Config.keyField,
      valueField: level2Config.valueField,
      sources: level2Config.sources,
      defaultSource: level2Config.defaultSource,
    })
  )
}

/** @deprecated Use loadGroupConfig instead */
export const loadGlobalConfig = loadGroupConfig

/**
 * Initialize a picker group with a specific topOperator.
 */
export const initializePickerGroup = (
  dispatch: Dispatch,
  groupId: string,
  topOperator: string = 'any'
): void => {
  dispatch(initializeGroup({ groupId }))
  dispatch(setGroupTopOperator({ groupId, operator: topOperator }))
}

/**
 * Initialize and populate a single picker with selection data.
 * This sets up the top selector, operator, and bottom selector for one picker.
 */
export const loadSinglePicker = (
  dispatch: Dispatch,
  groupId: string,
  pickerId: number,
  selection: ServerSelection
): void => {
  // Initialize picker state
  dispatch(initializePicker({ groupId, pickerId }))

  // Load top selector data and set selection
  const topItemKey = selection.level_1
  const topItemName = selection.level_1_label ?? topItemKey
  dispatch(
    loadTopData({
      groupId,
      pickerId,
      data: [{ id: topItemKey, name: topItemName }],
    })
  )
  dispatch(setTopSelectedItem({ groupId, pickerId, key: topItemKey }))

  // Enable bottom selector since we have a level_1 selection
  dispatch(setBottomDisabled({ groupId, pickerId, disabled: false }))

  // Set bottom operator (convert from backend format to show bottom selector)
  const uiOperator = fromBackendOperator(selection.operator)
  dispatch(setBottomOperator({ pickerId, operator: uiOperator }))

  // Load bottom selector data and set selections
  const bottomItemsData = selection.level_2.map(name => ({
    id: name,
    name: name,
  }))
  dispatch(
    loadBottomData({
      groupId,
      pickerId,
      data: bottomItemsData,
      totalCount: bottomItemsData.length,
    })
  )
  dispatch(
    setBottomSelectedItems({ groupId, pickerId, keys: selection.level_2 })
  )
}

/**
 * Load all pickers in a group from selections array.
 * Returns the highest picker ID for new picker generation.
 *
 * If selections array is empty, creates a single blank picker so users
 * don't have to manually add the first one (DATA-4313).
 *
 * @param startingPickerId - The first picker ID to use (ensures globally unique IDs)
 */
export const loadAllPickersInGroup = (
  dispatch: Dispatch,
  groupId: string,
  selections: ServerSelection[],
  startingPickerId: number = 0
): number => {
  // If no selections provided, create a blank picker for the user
  if (selections.length === 0) {
    dispatch(initializePicker({ groupId, pickerId: startingPickerId }))
    return startingPickerId
  }

  let highestPickerId = -1

  selections.forEach((selection, index) => {
    const pickerId = startingPickerId + index
    highestPickerId = Math.max(highestPickerId, pickerId)
    loadSinglePicker(dispatch, groupId, pickerId, selection)
  })

  return highestPickerId
}

/**
 * Load a complete picker group from server response.
 * Sets up group-specific config, initializes the group, and populates all pickers.
 *
 * @param startingPickerId - The first picker ID to use (get from selectNextPickerId)
 */
export const loadPickerGroup = (
  dispatch: Dispatch,
  groupId: string,
  serverResponse: ServerPickerResponse,
  startingPickerId: number = 0
): number => {
  const { picker_id, title, subtitle, level_1, level_2, selections } =
    serverResponse

  // Step 1: Initialize picker group with topOperator
  const topOperator = level_1.operator ?? 'any'
  initializePickerGroup(dispatch, groupId, topOperator)

  // Step 2: Set group pickerId (for submission payload)
  dispatch(setGroupPickerId({ groupId, pickerId: picker_id }))

  // Step 3: Set group title and subtitle
  dispatch(setGroupTitle({ groupId, title }))
  dispatch(setGroupSubtitle({ groupId, subtitle }))

  // Step 4: Load group-specific configuration
  // level_2 is guaranteed to exist for two-level pickers (callers check before calling)
  loadGroupConfig(dispatch, groupId, level_1, level_2!)

  // Step 5: Load all pickers in the group (using globally unique IDs)
  return loadAllPickersInGroup(dispatch, groupId, selections, startingPickerId)
}

// ═══════════════════════════════════════════════════════════════════════════
// Hydration Hook (for single group - backward compatible)
// ═══════════════════════════════════════════════════════════════════════════

interface HydrationResult {
  hasHydrated: boolean
  maxPickerId: number
}

/**
 * Hook to hydrate picker state from server response.
 * Orchestrates loading global config and all pickers within the default group.
 *
 * After hydration is complete, use selectPickerIdsForGroup from Redux
 * to get the current list of picker IDs (updates when pickers are added/removed).
 *
 * @param serverResponse The server response containing picker configuration
 * @param groupId Optional group ID (defaults to DEFAULT_GROUP_ID)
 * @returns Object with hasHydrated flag and maxPickerId for new picker ID generation
 */
export const useHydratePickersFromServer = (
  serverResponse: ServerPickerResponse | null,
  groupId: string = DEFAULT_GROUP_ID
): HydrationResult => {
  const dispatch = useDispatch()
  const nextPickerId = useSelector(selectNextAvailablePickerId)
  const hasHydratedRef = useRef(false)
  const [hasHydrated, setHasHydrated] = useState(false)
  const [maxPickerId, setMaxPickerId] = useState(-1)

  // Capture nextPickerId at mount to avoid stale closure issues
  const startingPickerIdRef = useRef(nextPickerId)

  useEffect(() => {
    // Only hydrate once
    if (!serverResponse || hasHydratedRef.current) return

    // Load the picker group with globally unique picker IDs
    const highestPickerId = loadPickerGroup(
      dispatch,
      groupId,
      serverResponse,
      startingPickerIdRef.current
    )

    // Mark hydration complete
    setMaxPickerId(highestPickerId)
    hasHydratedRef.current = true
    setHasHydrated(true)
  }, [serverResponse, groupId, dispatch])

  return { hasHydrated, maxPickerId }
}

// ═══════════════════════════════════════════════════════════════════════════
// Data Extraction Hook (for submission)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hook to extract current picker data from Redux for submission.
 * Returns a memoized function that extracts all picker selections for a group.
 *
 * Usage:
 *   const { extractGroupData } = useExtractGroupData(groupId)
 *   const handleSubmit = () => {
 *     const data = extractGroupData()
 *     // Submit data to server
 *   }
 */
export const useExtractGroupData = (groupId: string = DEFAULT_GROUP_ID) => {
  const pickerIds = useSelector((state: any) =>
    selectPickerIdsForGroup(state, groupId)
  )
  const topOperator = useSelector((state: any) =>
    selectGroupTopOperator(state, groupId)
  )
  const disabled = useSelector((state: any) =>
    selectGroupDisabled(state, groupId)
  )

  // We need to get state directly for extraction since we can't call
  // useSelector in a loop. Instead, we return a function that takes state.
  const extractGroupData = useCallback(
    (state: any): ExtractedGroupData => {
      const pickers: ExtractedPickerData[] = pickerIds.map(pickerId => {
        const level1Key = selectTopSelectedItem(state, pickerId)
        const level1Data = selectTopSelectedItemData(state, pickerId)
        const operator = selectBottomOperator(state, pickerId)
        const level2Keys = selectBottomSelectedItems(state, pickerId)
        const level2Data = selectBottomSelectedItemsData(state, pickerId)

        return {
          pickerId,
          level1Key,
          level1Name: level1Data?.name ?? null,
          operator,
          level2Keys,
          level2Names: Object.values(level2Data).map(item => item.name),
        }
      })

      return {
        groupId,
        topOperator,
        disabled,
        pickers,
      }
    },
    [groupId, pickerIds, topOperator, disabled]
  )

  return { extractGroupData, pickerIds, topOperator, disabled }
}

/**
 * Simpler hook variant that returns extracted data directly (reactive).
 * Re-renders when any picker selection changes.
 *
 * Usage:
 *   const { topOperator, pickers, disabled } = useGroupData(groupId)
 */
export const useGroupData = (
  groupId: string = DEFAULT_GROUP_ID
): ExtractedGroupData => {
  const pickerIds = useSelector((state: any) =>
    selectPickerIdsForGroup(state, groupId)
  )
  const topOperator = useSelector((state: any) =>
    selectGroupTopOperator(state, groupId)
  )
  const disabled = useSelector((state: any) =>
    selectGroupDisabled(state, groupId)
  )

  // For each picker, we need individual selectors
  // This is a simplified version - for many pickers, consider memoization
  const pickers = useSelector((state: any): ExtractedPickerData[] =>
    pickerIds.map(pickerId => {
      const level1Key = selectTopSelectedItem(state, pickerId)
      const level1Data = selectTopSelectedItemData(state, pickerId)
      const operator = selectBottomOperator(state, pickerId)
      const level2Keys = selectBottomSelectedItems(state, pickerId)
      const level2Data = selectBottomSelectedItemsData(state, pickerId)

      return {
        pickerId,
        level1Key,
        level1Name: level1Data?.name ?? null,
        operator,
        level2Keys,
        level2Names: Object.values(level2Data).map(item => item.name),
      }
    })
  )

  return { groupId, topOperator, disabled, pickers }
}

// ═══════════════════════════════════════════════════════════════════════════
// Pre-Submission Cleanup
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Clean up pickers before building submission payload.
 * - Removes pickers with no TopSelector selection
 * - Updates operator to a non-value-requiring one for pickers with no BottomSelector selections
 *
 * @param dispatch - Redux dispatch function
 * @param state - Redux root state
 * @param groupId - The group ID to clean up
 */
export const cleanupPickersBeforeSubmit = (
  dispatch: Dispatch,
  state: any,
  groupId: string
): void => {
  const pickerIds = selectPickerIdsForGroup(state, groupId)

  for (const pickerId of pickerIds) {
    const level1Key = selectTopSelectedItem(state, pickerId)

    // Remove pickers with no top selection
    if (level1Key === null) {
      dispatch(removePicker({ pickerId }))
      continue
    }

    // Check if picker has bottom selections
    const level2Keys = selectBottomSelectedItems(state, pickerId)
    const bottomSelectionEmpty = level2Keys.length === 0

    // Get current operator and convert to backend format if needed
    const currentOperator = selectBottomOperator(state, pickerId)
    const backendOperator = toBackendOperator(
      currentOperator,
      bottomSelectionEmpty
    )

    // Update operator if it changed (e.g., 'any' → 'skip' when no selections)
    if (backendOperator !== currentOperator) {
      dispatch(setBottomOperator({ pickerId, operator: backendOperator }))
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Submission Payload Builder
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build the submission payload from Redux state.
 * This is a pure function that takes state and returns the payload.
 *
 * @param state - Redux root state
 * @param groupId - The group ID to build payload for
 * @returns PickerSubmissionPayload ready for AIConversation
 */
export const buildSubmissionPayload = (
  state: any,
  groupId: string
): PickerSubmissionPayload => {
  // Get group-level data
  const pickerId = selectGroupPickerId(state, groupId)
  const topOperator = selectGroupTopOperator(state, groupId)
  const level1Id = selectGroupLevel1Id(state, groupId)
  const level2Id = selectGroupLevel2Id(state, groupId)
  const level2OperatorOptions = selectGroupLevel2OperatorOptions(state, groupId)

  // Get all pickers in this group
  const pickerIds = selectPickerIdsForGroup(state, groupId)

  // Collect all unique level1 selection IDs
  const level1SelectionIds: string[] = []
  const seenLevel1: Set<string> = new Set()

  // Build parent_selections array
  const parentSelections: ParentSelection[] = []

  for (const pid of pickerIds) {
    const level1Key = selectTopSelectedItem(state, pid)
    if (level1Key === null) continue

    // Add to level1 selection_ids (de-duped)
    if (!seenLevel1.has(level1Key)) {
      seenLevel1.add(level1Key)
      level1SelectionIds.push(level1Key)
    }

    // Get picker's bottom state
    const operator = selectBottomOperator(state, pid)
    const level2Keys = selectBottomSelectedItems(state, pid)

    // Check if this operator requires values
    const operatorOption = level2OperatorOptions.find(
      (opt: OperatorOption) => opt.id === operator
    )
    const requiresValues = operatorOption?.requiresValues !== false

    // Build parent selection
    const parentSelection: ParentSelection = {
      parent_id: level1Key,
      operator,
    }

    // Only include child_ids if operator requires values
    if (requiresValues) {
      parentSelection.child_ids = level2Keys
    }

    parentSelections.push(parentSelection)
  }

  // Build the payload
  const levels: PickerLevelSubmission[] = [
    {
      id: level1Id,
      operator: topOperator,
      selection_ids: level1SelectionIds,
    },
    {
      id: level2Id,
      operator: level2OperatorOptions[0]?.id ?? 'any',
      selection_ids: [],
      ...(parentSelections.length > 0 && {
        parent_selections: parentSelections,
      }),
    },
  ]

  return {
    picker_id: pickerId,
    levels,
  }
}

/**
 * Hook to get the submission payload for a group.
 * Returns a function that builds the payload from current state.
 */
export const useBuildSubmissionPayload = (
  groupId: string = DEFAULT_GROUP_ID
) => {
  const buildPayload = useCallback(
    (state: any): PickerSubmissionPayload =>
      buildSubmissionPayload(state, groupId),
    [groupId]
  )

  return { buildPayload }
}

// ═══════════════════════════════════════════════════════════════════════════
// Legacy Hooks (backward compatible aliases)
// ═══════════════════════════════════════════════════════════════════════════

/** @deprecated Use useExtractGroupData instead */
export const useExtractPickersData = () => {
  const { extractGroupData, pickerIds } = useExtractGroupData(DEFAULT_GROUP_ID)
  return {
    extractPickersData: extractGroupData,
    pickerIds,
  }
}

/** @deprecated Use useGroupData instead */
export const usePickersData = () => {
  const { topOperator, pickers } = useGroupData(DEFAULT_GROUP_ID)
  return { topOperator, pickers }
}

// ═══════════════════════════════════════════════════════════════════════════
// Sample Server Response (for testing)
// ═══════════════════════════════════════════════════════════════════════════

export const SAMPLE_SERVER_RESPONSE: ServerPickerResponse = {
  picker_id: 'picker_7783480ecdc6d4ac',
  type: 'items_with_modifiers',
  title: 'Review items',
  subtitle: 'Must include {operator} of the following items',
  level_1: {
    id: 'items',
    label: 'Item',
    selectionType: 'select',
    operatorOptions: [
      { id: 'any', text: 'any' },
      { id: 'all', text: 'all' },
      { id: 'none', text: 'none' },
    ],
    endpoint: '/api/v1/merchants/:merchant_id/snowflake/query',
    params: {
      table_name: 'te_order_items',
      fields: ['item_name'],
      distinct: true,
      search_field: 'item_name',
    },
    keyField: 'item_name',
    valueField: 'item_name',
    operator: 'any',
  },
  level_2: {
    id: 'modifiers',
    label: 'Modifiers',
    selectionType: 'multiselect',
    operatorOptions: [
      { id: 'any', text: 'any of these', requiresValues: true },
      { id: 'all', text: 'all of these', requiresValues: true },
      { id: 'none', text: 'none of these', requiresValues: true },
      { id: 'empty', text: 'no', requiresValues: false },
      { id: 'skip', text: 'any', requiresValues: false },
    ],
    endpoint: '/api/v1/merchants/:merchant_id/snowflake/query',
    params: {
      table_name: 'te_item_modifier_values',
      fields: ['modifier_name'],
      distinct: true,
      search_field: 'modifier_name',
      filter_field: 'item_name',
      filter_value: ':level_1',
    },
    keyField: 'modifier_name',
    valueField: 'modifier_name',
    subtitle: 'with {operator} modifiers',
  },
  selections: [
    {
      level_1: 'Candied Bacon Burger',
      operator: 'any',
      level_2: ['Onion Rings'],
    },
    {
      level_1: "Kid's Cheeseburger",
      operator: 'any',
      level_2: ['Onion Rings'],
    },
    {
      level_1: "Kid's Hamburger",
      operator: 'any',
      level_2: ['Onion Rings'],
    },
    {
      level_1: "Sonny's Steakburger*",
      operator: 'any',
      level_2: ['Onion Rings'],
    },
  ],
}
